// PIREPs (Pilot Reports) layer family.
//
// Pilot reports of in-flight conditions. Color-coded by hazard class.
// PIREPs are point-in-time observations — the time slider hides reports
// older than the slider's center minus 2h, so scrubbing back/forward
// replays the report stream. One GeoJSON source drives two layers: a
// symbol icon layer (colored eyeball glyphs per hazard class) and a
// symbol label layer (altitude in "FL130" style, minzoom 7).
//
// Icons are registered via ensurePirepIcons (called inside mount, just
// like wxstations calls ensureWindBarbIcons). The icon expression
// PIREP_ICON_EXPRESSION maps the hazard property to the correct glyph.
// Both helpers live in ./pirepIcons (moved here from addOverlays.ts which
// no longer needs them after this extraction).
//
// Extracted from addOverlays.ts as HI-1 batch 7 round 2 (2026-05-24).
// Pure mechanical move — source spec, layout/paint expressions, layer ids,
// minzoom, and layer order are byte-identical to the pre-extraction form.
//
// applyState reads visibility from useUiStore so a basemap setStyle()
// rebuild restores the user's current toggle state transparently.

import type { Map as MlMap } from 'maplibre-gl';
import { EMPTY_FC } from '../MapView';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { ensurePirepIcons, PIREP_ICON_EXPRESSION } from '../pirepIcons';
import { isOverlayLoaded, markOverlayLoaded, refreshOverlay } from '../overlayRuntime';
import { OVERLAY_BY_ID } from '../overlaySpecs';

export function addPirepsLayers(map: MlMap): void {
  if (!map.getSource('pireps')) {
    map.addSource('pireps', { type: 'geojson', data: EMPTY_FC });
    // PIREP icons: colored eyeball glyphs (one per hazard class).
    // Replaces the previous dark-halo + colored-fill circle stack.
    // The eyeball glyph carries the 'pilot saw it with their own
    // eyes' semantic; iris color preserves the hazard-class code
    // so the existing visual scan still works. See pirepIcons.ts
    // for the SVG geometry + color mapping.
    void ensurePirepIcons(map);
    map.addLayer({
      id: 'pireps-icon',
      type: 'symbol',
      source: 'pireps',
      layout: {
        visibility: 'none',
        'icon-image': PIREP_ICON_EXPRESSION,
        // Zoom-adaptive icon size. Bumped 2026-05-29 (boss: "the
        // eyeball is hard to see, make it bigger and add a colored
        // ring around it"). The SVG source is now 40 px (was 32),
        // and the on-map scale ramp is also nudged up so the ring
        // is large enough to be the dominant visual cue at en-route
        // zooms. 0.45 → 0.70 → 0.95 against 40 px = 18 → 28 → 38 px
        // on-screen, vs. 11 → 18 → 24 px before.
        'icon-size': [
          'interpolate', ['linear'], ['zoom'],
          4, 0.45, 7, 0.70, 11, 0.95,
        ],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    });
    // Altitude label — "FL130" style. Only shows at zoom 7+ to keep
    // the en-route view uncluttered.
    map.addLayer({
      id: 'pireps-label',
      type: 'symbol',
      source: 'pireps',
      minzoom: 7,
      layout: {
        visibility: 'none',
        'text-field': [
          'case',
          ['has', 'fltlvl'],
          ['concat', 'FL', ['to-string', ['get', 'fltlvl']]],
          '',
        ],
        'text-size': 9,
        'text-anchor': 'top',
        'text-offset': [0, 0.9],
        'text-font': ['Noto Sans Regular'],
        'text-allow-overlap': false,
        'text-ignore-placement': false,
      },
      paint: {
        'text-color': '#e5e7eb',
        'text-halo-color': '#0b0d0f',
        'text-halo-width': 1.4,
      },
    });
  }
}

registerLayerFamily({
  id: 'pireps',
  label: 'PIREPs',
  sourceId: 'pireps',
  layerIds: ['pireps-icon', 'pireps-label'],
  mount: (map) => addPirepsLayers(map),
  visibilitySelector: (l) => l.pireps,
  applyState: (map) => {
    if (!map.getLayer('pireps-icon')) return;
    const show = useLayerVisibilityStore.getState().layers.pireps;
    const v = show ? 'visible' : 'none';
    map.setLayoutProperty('pireps-icon', 'visibility', v);
    map.setLayoutProperty('pireps-label', 'visibility', v);
  },
  onToggleOn: (map) => {
    const spec = OVERLAY_BY_ID.get('pireps');
    if (!spec || isOverlayLoaded('pireps')) return;
    markOverlayLoaded('pireps');
    return refreshOverlay(map, spec);
  },
  bboxRefresh: (map) => {
    if (!isOverlayLoaded('pireps')) return;
    const spec = OVERLAY_BY_ID.get('pireps');
    if (!spec) return;
    return refreshOverlay(map, spec);
  },
});
